'use client';

import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { ConsentScriptsContext } from './consent-scripts-context';
import type { ConsentScriptsContextValue } from './consent-scripts-provider.types';
import { syncConsentInputs } from './sync-consent-inputs';

/**
 * Consent scripts state for a PII plate, or `undefined` when no
 * `ConsentScriptsProvider` is mounted. When `undefined`, the plate must load
 * the scripts itself exactly as before; when defined, it must not render its
 * own Jornaya/TrustedForm `<Script>` or assign the `window` callbacks.
 *
 * With a provider, this also:
 * - calls `notifyPiiMounted` once (`consent-tokens-at-pii`; no `reInit`),
 * - exposes the token captured on the first plate, the same one for every
 *   later visit to this plate, and
 * - keeps the plate's hidden `universal_leadid` / `xxTrustedFormCertUrl`
 *   inputs populated with that token and the captured certificate.
 *
 * Call it from the component that renders the PII form (or one of its
 * ancestors), so the form is in the DOM when the effects run.
 */
export function usePiiConsentScripts(): ConsentScriptsContextValue | undefined {
  const consent = useContext(ConsentScriptsContext);
  const notifyPiiMounted = consent?.notifyPiiMounted;
  const notifiedRef = useRef(false);
  const [mountId, setMountId] = useState<number>();

  useEffect(() => {
    if (notifyPiiMounted && !notifiedRef.current) {
      notifiedRef.current = true;
      setMountId(notifyPiiMounted());
    }
  }, [notifyPiiMounted]);

  const hasProvider = consent !== undefined;
  const piiMount = consent?.piiMount;
  const isCurrent =
    mountId !== undefined && piiMount?.id === mountId && piiMount.refreshed;
  const leadId = isCurrent ? consent?.jornaya.token : undefined;
  const certUrl = consent?.trustedForm.certUrl;

  useEffect(() => {
    if (hasProvider) syncConsentInputs(document, { leadId, certUrl });
  }, [hasProvider, leadId, certUrl]);

  return useMemo(
    () =>
      consent && { ...consent, jornaya: { ...consent.jornaya, token: leadId } },
    [consent, leadId],
  );
}
